import { cn } from 'component-library/utils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CLIMATE_RISK_FACT_TYPES,
  ClimateRiskRatingOrder,
  IndicatorFactType,
  PlotReportFactLabelNameEnum,
  Scenario,
} from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { GraphFact } from '@/components/Charts/types';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';
import { isNullish } from '@/utils/booleans';
import { safeRiskIndexToRating } from '@/utils/climateRisk';
import { printYear } from '@/utils/formatting/date';

// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '../widgets/WidgetSlotRenderer';

type RiskChartProps = {
  riskFactType: CLIMATE_RISK_FACT_TYPES | null;
};

export const RiskChart = ({ riskFactType }: RiskChartProps) => {
  const { plotId } = useParams<{ plotId: string }>();

  if (isNullish(riskFactType)) {
    return <NoData />;
  }

  if (plotId) {
    return <RiskChartPlots riskFactType={riskFactType} />;
  }

  return <RiskChartOrganization riskFactType={riskFactType} />;
};

const RiskChartPlots = ({ riskFactType }: { riskFactType: CLIMATE_RISK_FACT_TYPES }) => {
  const riskFactName = `${riskFactType}_graph`;

  const facts = usePlotFacts([riskFactType, riskFactName], null);
  const risk = getFactFromFactArray<number>(facts, riskFactType, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });

  const getRatingNumberFromRating = (value: Record<string, string | number> | unknown) => {
    return !isNullish(value) && !isNullish((value as Record<string, string | number>).rating)
      ? ClimateRiskRatingOrder[(value as Record<string, string | number>).rating as keyof typeof ClimateRiskRatingOrder]
      : 0;
  };

  const baselineScenarioGraphFact = getFactFromFactArray<GraphFact[]>(facts, riskFactName, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });

  const fallbackBaselineScenarioGraphFact = [
    {
      name: risk?.name,
      date: risk?.measured_at,
      unit: risk?.unit,
      metadata: risk?.metadata,
      labels: risk?.labels,
      value: getRatingNumberFromRating(risk?.value),
      upper_confidence_limit: risk?.upper_confidence_limit,
      lower_confidence_limit: risk?.lower_confidence_limit,
      confidence_interval_pct: null,
    },
  ] as GraphFact[];

  const finalBaselineScenarioGraphFact = baselineScenarioGraphFact?.value ?? fallbackBaselineScenarioGraphFact;

  const baselineScenarioGraphFactValue = finalBaselineScenarioGraphFact
    .map((fact) => {
      return { ...fact, value: getRatingNumberFromRating(fact.value), name: `${fact.name}_baseline` };
    })
    .concat(finalBaselineScenarioGraphFact.map((fact) => ({ ...fact, name: `${fact.name}_baseline` })) ?? []);
  const bestScenarioGraphFact = getFactFromFactArray<GraphFact[]>(facts, riskFactName, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.best,
  });
  const bestScenarioGraphFactValue = (bestScenarioGraphFact?.value ?? [])
    .map((fact) => {
      return { ...fact, value: getRatingNumberFromRating(fact.value), name: `${fact.name}_best` };
    })
    .concat(finalBaselineScenarioGraphFact.map((fact) => ({ ...fact, name: `${fact.name}_best` })) ?? []);
  const moderateScenarioGraphFact = getFactFromFactArray<GraphFact[]>(facts, riskFactName, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.moderate,
  });
  const moderateScenarioGraphFactValue = (moderateScenarioGraphFact?.value ?? [])
    .map((fact) => {
      return { ...fact, value: getRatingNumberFromRating(fact.value), name: `${fact.name}_moderate` };
    })
    .concat(finalBaselineScenarioGraphFact.map((fact) => ({ ...fact, name: `${fact.name}_moderate` })) ?? []);
  const worsteScenarioGraphFact = getFactFromFactArray<GraphFact[]>(facts, riskFactName, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.worst,
  });
  const worsteScenarioGraphFactValue = (worsteScenarioGraphFact?.value ?? [])
    .map((fact) => {
      return { ...fact, value: getRatingNumberFromRating(fact.value), name: `${fact.name}_worst` };
    })
    .concat(finalBaselineScenarioGraphFact.map((fact) => ({ ...fact, name: `${fact.name}_worst` })) ?? []);

  if (isNullish(risk) || isNullish(risk.value)) {
    return null;
  }

  const chartData = [
    ...baselineScenarioGraphFactValue,
    ...bestScenarioGraphFactValue,
    ...moderateScenarioGraphFactValue,
    ...worsteScenarioGraphFactValue,
  ];
  return <RiskChartBase chartData={chartData} riskFactType={riskFactType} />;
};

const RiskChartOrganization = ({ riskFactType }: { riskFactType: CLIMATE_RISK_FACT_TYPES }) => {
  const { [riskFactType]: risk } = useOrganizationIndicatorReportingFact(riskFactType, null, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });

  const riskFactName = `${riskFactType}_graph` as IndicatorFactType;

  const { [riskFactName]: baselineScenarioGraphFact } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    riskFactName,
    null,
    {
      [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
    },
  );
  const fallbackBaselineScenarioGraphFact = [
    {
      name: risk?.name,
      date: risk?.measured_at,
      unit: risk?.unit,
      metadata: risk?.metadata,
      labels: risk?.labels,
      value: risk?.value,
      upper_confidence_limit: risk?.upper_confidence_limit,
      lower_confidence_limit: risk?.lower_confidence_limit,
      confidence_interval_pct: null,
    },
  ] as GraphFact[];

  const finalBaselineScenarioGraphFact = baselineScenarioGraphFact?.value ?? fallbackBaselineScenarioGraphFact;

  const baselineScenarioGraphFactValue = finalBaselineScenarioGraphFact
    .map((fact) => {
      return { ...fact, name: `${fact.name}_baseline` };
    })
    .concat(finalBaselineScenarioGraphFact.map((fact) => ({ ...fact, name: `${fact.name}_baseline` })) ?? []);
  const { [riskFactName]: bestScenarioGraphFact } = useOrganizationIndicatorGraphFact<GraphFact[]>(riskFactName, null, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.best,
  });
  const bestScenarioGraphFactValue = (bestScenarioGraphFact?.value ?? [])
    .map((fact) => {
      return { ...fact, name: `${fact.name}_best` };
    })
    .concat(finalBaselineScenarioGraphFact.map((fact) => ({ ...fact, name: `${fact.name}_best` })) ?? []);
  const { [riskFactName]: moderateScenarioGraphFact } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    riskFactName,
    null,
    {
      [PlotReportFactLabelNameEnum.scenario]: Scenario.moderate,
    },
  );
  const moderateScenarioGraphFactValue = (moderateScenarioGraphFact?.value ?? [])
    .map((fact) => {
      return { ...fact, name: `${fact.name}_moderate` };
    })
    .concat(finalBaselineScenarioGraphFact.map((fact) => ({ ...fact, name: `${fact.name}_moderate` })) ?? []);
  const { [riskFactName]: worsteScenarioGraphFact } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    riskFactName,
    null,
    {
      [PlotReportFactLabelNameEnum.scenario]: Scenario.worst,
    },
  );
  const worsteScenarioGraphFactValue = (worsteScenarioGraphFact?.value ?? [])
    .map((fact) => {
      return { ...fact, name: `${fact.name}_worst` };
    })
    .concat(finalBaselineScenarioGraphFact.map((fact) => ({ ...fact, name: `${fact.name}_worst` })) ?? []);

  if (isNullish(risk) || isNullish(risk.value)) {
    return null;
  }

  const chartData = [
    ...baselineScenarioGraphFactValue,
    ...bestScenarioGraphFactValue,
    ...moderateScenarioGraphFactValue,
    ...worsteScenarioGraphFactValue,
  ];

  return <RiskChartBase chartData={chartData} riskFactType={riskFactType} />;
};

type RiskChartBaseProps = {
  riskFactType: CLIMATE_RISK_FACT_TYPES;
  chartData: GraphFact[];
};

const RiskChartBase = ({ riskFactType, chartData }: RiskChartBaseProps) => {
  const { t } = useTranslation();
  const noBorderClass = 'border-none';
  const circleIcon = 'ri-circle-fill';

  const labels = {
    baseline: t('climateRisk.scenarios.baseline.title'),
    worstCase: t('climateRisk.scenarios.worst.title'),
    moderate: t('climateRisk.scenarios.moderate.title'),
    best: t('climateRisk.scenarios.best.title'),
  };

  const commonLegendProps = {
    icon: circleIcon,
    className: 'bg-neutral-black-4 p-2 rounded-lg',
  };

  const config = {
    name: 'default_risk_graph_card',
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      header: t(`climateRisk.riskTypes.${riskFactType}.title`),
      styles: {
        root: noBorderClass,
        header: 'hidden',
        body: cn('p-6 pb-2 bg-neutral-black-4 rounded-2xl', noBorderClass),
      },
    },
    widgets: [
      {
        name: 'default_risk_graph',
        type: WidgetType.o_composite_graph,
        visible: true,
        relatedData: {
          data: chartData,
          chartConfig: [
            {
              shape: ChartShape.line,
              id: `${riskFactType}_baseline`,
              key: `${riskFactType}_baseline`,
              dataKey: `${riskFactType}_baseline.value`,
              strokeWidth: 2,
              label: (
                <span className='gap-1'>
                  <i className='ri-circle-fill text-[#ff6b6b] pr-1 w-1'></i>
                  <span>{labels.baseline}</span>
                </span>
              ),
              color: '#ff6b6b',
            },
            {
              shape: ChartShape.line,
              id: `${riskFactType}_worst`,
              key: `${riskFactType}_worst`,
              dataKey: `${riskFactType}_worst.value`,
              strokeWidth: 2,
              label: (
                <span className='gap-1'>
                  <i className='ri-circle-fill text-[#D83B09] pr-1 w-1'></i>
                  <span>{labels.worstCase}</span>
                </span>
              ),
              color: '#D83B09',
            },
            {
              shape: ChartShape.line,
              id: `${riskFactType}_moderate`,
              key: `${riskFactType}_moderate`,
              dataKey: `${riskFactType}_moderate.value`,
              strokeWidth: 2,
              label: (
                <span className='gap-1'>
                  <i className='ri-circle-fill text-[#FFAA1E] pr-1 w-[1px]'></i>
                  <span>{labels.moderate}</span>
                </span>
              ),
              color: '#FFAA1E',
            },
            {
              shape: ChartShape.line,
              id: `${riskFactType}_best`,
              key: `${riskFactType}_best`,
              dataKey: `${riskFactType}_best.value`,
              strokeWidth: 2,
              label: (
                <span className='gap-1 flex-row'>
                  <i className='ri-circle-fill text-[#FFDD5F] pr-1 w-1'></i>
                  <span>{labels.best}</span>
                </span>
              ),
              color: '#FFDD5F',
            },
          ],
          legendItems: [
            {
              ...commonLegendProps,
              label: labels.best,
              color: '#FFDD5F',
              popoverContent: {
                title: labels.best,
                body: t('climateRisk.scenarios.best.tooltip.body'),
              },
            },

            {
              ...commonLegendProps,
              label: labels.moderate,
              color: '#FFAA1E',
              popoverContent: {
                title: labels.moderate,
                body: t('climateRisk.scenarios.moderate.tooltip.body'),
              },
            },
            {
              ...commonLegendProps,
              label: labels.worstCase,
              color: '#ff6b6b',
              popoverContent: {
                title: labels.worstCase,
                body: t('climateRisk.scenarios.worst.tooltip.body'),
              },
            },
          ],
          xAxisProps: {
            dataKey: 'label',
            tickFormatter: printYear,
            domain: ['dataMin', 'dataMax'],
          },
          yAxisProps: {
            label: t('climateRisk.risk'),
            tickFormatter: (value: number) => {
              const rating = safeRiskIndexToRating(value);
              return t(`global.widget.filters.sections.risks.ratings.${rating}`);
            },
            tickMargin: 5,
            domain: [0, 5],
          },
        },
      },
    ],
  };

  return <WidgetSlotRenderer nestedWidget={config} />;
};
